import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

// PrimeReact
import FilterBank from "./FilterBank";
import { BreadCrumb } from "primereact/breadcrumb";
import {Column} from 'primereact/column';

// Services
import { getAccount } from "../../../services/GlobalRoles/getAccount";
import { postItauPaidQuery } from "../../../services/Banks/postItauPaidQuery";
import { postSicrediPaidQuery } from "../../../services/Banks/postSicrediPaidQuery";
import { postBradescoPaidQuery} from "../../../services/Banks/postBradescoPaidQuery";
// Utils

// Context
import { useAppContext } from "../../../context/AppContext";
import usePermission from "../../../hooks/usePermission";
import { DataTable } from "primereact/datatable";
import TableBankSync from "./TableBankSync";

export default function BanksSync() {
  const { permissions } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [syncData, setSyncData] = useState(null);
  const [showSyncTable, setShowSyncTable] = useState(false);
  const [totalSyncRecords, setTotalSyncRecords] = useState(0);

  const getErrorMessage = (error) => {
    if (!error) return "Erro desconhecido";
    
    const errorMessages = {
      "Recurso não encontrado": "Não foram encontradas transações para os filtros selecionados.",
      "Unauthorized": "Você não tem permissão para acessar este recurso.",
      "Network Error": "Erro de conexão. Verifique sua internet e tente novamente.",
      "Timeout": "A sincronização demorou mais que o esperado. Tente novamente.",
      "Internal Server Error": "Erro interno do servidor. Tente novamente em alguns minutos.",
      "Bad Request": "Dados inválidos. Verifique as informações e tente novamente.",
      "Forbidden": "Acesso negado. Entre em contato com o administrador.",
      "Service Unavailable": "Serviço temporariamente indisponível. Tente novamente em alguns minutos.",
      "Conta não encontrada": "A conta bancária selecionada não foi encontrada."
    };
    
    // console.log("Erro recebido:", error); 
    
    if (typeof error === "string") {
      return errorMessages[error] || error;
    }
    
    if (error.response && error.response.data) {
      if (error.response.data.error) {
        return errorMessages[error.response.data.error] || error.response.data.error;
      }
      if (error.response.data.message) {
        return errorMessages[error.response.data.message] || error.response.data.message;
      }
      if (typeof error.response.data === "string") {
        return errorMessages[error.response.data] || error.response.data;
      }
    }
    
    if (error.error) {
      return errorMessages[error.error] || error.error;
    }
    
    if (error.message) {
      return errorMessages[error.message] || error.message;
    }
    
    if (typeof error === "object" && error !== null) {
      const errorKeys = ["error", "message", "msg", "detail", "description"];
      for (const key of errorKeys) {
        if (error[key] && typeof error[key] === "string") {
          return errorMessages[error[key]] || error[key];
        }
      }
    }
    
    if (error.status || (error.response && error.response.status)) {
      const status = error.status || error.response.status;
      switch (status) {
        case 404:
          return "Não foram encontradas transações para os filtros selecionados.";
        case 401:
          return "Você não tem permissão para acessar este recurso.";
        case 403:
          return "Acesso negado. Entre em contato com o administrador.";
        case 500:
          return "Erro interno do servidor. Tente novamente em alguns minutos.";
        case 503:
          return "Serviço temporariamente indisponível. Tente novamente em alguns minutos.";
        default:
          return "Erro desconhecido";
      }
    }
    
    return "Erro desconhecido";
  };

  usePermission("160");

  const breadcrumbItems = [
    { label: "Financeiro" },
    { label: "Sincronização Bancária" },
  ];

  const form = useForm({
    defaultValues: {
      account_id: "",
      filterType: "",
      date: "",
      start_date: "",
      end_date: "",
      our_number: "",
    },
  });
  const errors = form.formState.errors;

  const { data: accounts, isLoading: isLoadingAccounts } = useQuery({
    queryKey: ["accounts"],
    queryFn: () => getAccount(),
    enabled: !!permissions?.includes("145"),
  });

  const watchedFields = form.watch();

  const isButtonDisabled = () => {
    const { account_id, filterType, date, start_date, end_date, our_number } = watchedFields;

    if (!account_id) return true;

    const selectedAccount = accounts?.find(
      (account) => account.id === account_id
    );

    if (filterType === "date") {
      if (selectedAccount?.bank === "237") {
        return !start_date || !end_date;
      }
      return !date;
    } else if (filterType === "our_number") {
      return !our_number;
    }
  };

  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return false;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (end < start) {
      return { isValid: false, message: "A data final deve ser maior que a data inicial!" };
    }
    
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 7) {
      return { isValid: false, message: "O período máximo permitido é de 7 dias!" };
    }
    
    return { isValid: true };
  };

  const syncBankInstallments = async (accountId, filterData) => {
    try {
      // console.log("Account ID:", accountId);
      // console.log("Filter Data:", filterData);

      const selectedAccount = accounts?.find(
        (account) => account.id === accountId
      );

      if (!selectedAccount) {
        throw new Error("Conta não encontrada");
      }

      console.log("Selected Account:", selectedAccount);

      let result = [];

      let payload = {};

      const formatDateForAPI = (date) => {
        if (!date) return null;
        if (typeof date === "string") return date;
        if (date instanceof Date) {
          return date.toISOString().split("T")[0];
        }
        return date;
      };

      if (filterData.filterType === "date") {
        if (selectedAccount.bank === "237") {
          // Para Bradesco, usar date_start e date_end (nomes esperados pelo backend)
          if (filterData.start_date && filterData.end_date) {
            payload = {
              type: 1,
              date_start: formatDateForAPI(filterData.start_date),
              date_end: formatDateForAPI(filterData.end_date),
              current_account_id: accountId,
            };
          }
        } else {
          // Para outros bancos, usar date
          if (filterData.date) {
            payload = {
              type: 1,
              date: formatDateForAPI(filterData.date),
              current_account_id: accountId,
            };
          }
        }
      } else if (
        filterData.filterType === "our_number" &&
        filterData.our_number
      ) {
        payload = {
          type: 2,
          installment_id: filterData.our_number,
        };
      } else {
        payload = {
          type: 1,
          current_account_id: accountId,
        };
      }
      console.log("Payload to send:", payload);

      if (selectedAccount.bank === "341") {
        result = await postItauPaidQuery(payload);
      } else if (selectedAccount.bank === "748") {
        result = await postSicrediPaidQuery(payload);
      } else if (selectedAccount.bank === "237") {
        result = await postBradescoPaidQuery(payload);
      } else {
        const supportedBanks = {
          341: "Banco Itaú",
          748: "Banco Sicredi",
          237: "Banco Bradesco",
        };

        const supportedBanksList = Object.values(supportedBanks).join(", ");
        throw new Error(
          `Sincronização não disponível para o banco ${selectedAccount.name_bank}. Bancos suportados: ${supportedBanksList}`
        );
      }

      return result;
    } catch (error) {
      console.error("Erro ao sincronizar parcelas:", error);
      throw error;
    }
  };

  const onSyncClick = async () => {
    const data = form.getValues();

    if (!data.account_id) {
      toast.warning("Selecione uma conta bancária!");
      return;
    }

    if (!data.filterType) {
      toast.warning("Selecione um tipo de filtro (Data ou Nosso Número)!");
      return;
    }

    const selectedAccount = accounts?.find(
      (account) => account.id === data.account_id
    );

    if (data.filterType === "date") {
      if (selectedAccount?.bank === "237") {
        // Validações específicas do Bradesco
        if (!data.start_date || !data.end_date) {
          toast.warning("Preencha as datas inicial e final para filtrar por período!");
          return;
        }

        const validation = validateDateRange(data.start_date, data.end_date);
        if (!validation.isValid) {
          toast.warning(validation.message);
          return;
        }
      } else {
        // Validação para outros bancos
        if (!data.date) {
          toast.warning("Preencha a data para filtrar por data!");
          return;
        }
      }
    }

    if (data.filterType === "our_number" && !data.our_number) {
      toast.warning("Preencha o nosso número para filtrar!");
      return;
    }

    setLoading(true);
    const notification = toast.loading("Sincronizando com o banco...");

    try {
      const result = await syncBankInstallments(data.account_id, data);

      // console.log("Sync result:", result);
      const resultArray = Array.isArray(result)
        ? result
        : result?.data
        ? result.data
        : [];

      setSyncData(resultArray);
      setTotalSyncRecords(resultArray.length);
      setShowSyncTable(true);
      toast.dismiss(notification);
      
      // Usar mensagem específica da API apenas quando NÃO há dados retornados
      const successMessage = (resultArray.length === 0 && result?.success) ? result.success : "Sincronização concluída!";
      toast.success(successMessage);
    } catch (error) {
      console.error("Erro na sincronização:", error);
      toast.dismiss(notification);
      
      const friendlyErrorMessage = getErrorMessage(error);
      toast.error(`Erro ao sincronizar: ${friendlyErrorMessage}`);
      
      setSyncData([]);
      setTotalSyncRecords(0);
      setShowSyncTable(false);
    } finally {
      setLoading(false);
    }
  };

  const home = {
    template: () => (
      <Link to={"#"} className="p-breadcrumb-link">
        <i className="fa fa-home"></i>
      </Link>
    ),
  };

  return (
    <>
      <div className="d-flex justify-content-between mb-3">
        <BreadCrumb
          model={breadcrumbItems}
          home={home}
          style={{
            background: "transparent",
            border: "none",
            fontSize: "16px",
          }}
        />
      </div>

      <div className="row">
        <div className="col-xl-12 mb-3">
          {/* {console.log(
            "Contas filtradas (homologation === 1):",
            accounts?.filter(
              (acc) =>
                acc.homologation === 1 &&
                (acc.bank === "748" || acc.bank === "341")
            )
          )} */}
          <FilterBank
            form={form}
            isLoadingAccounts={isLoadingAccounts}
            accounts={accounts?.filter(
              (acc) =>
                acc.homologation === 1 &&
                (acc.bank === "748" || acc.bank === "341" || acc.bank === "237")
            )}
            onSyncClick={onSyncClick}
            loading={loading}
            errors={errors}
            isButtonDisabled={isButtonDisabled}
          />
        </div>

        <div className="col-xl-12">
          {showSyncTable && (
           <TableBankSync 
             syncData={syncData} 
             totalRecords={totalSyncRecords}
             currentAccountName={(() => {
               const account = accounts?.find(acc => acc.id === watchedFields.account_id);
               return account ? `${account.name_bank}_${account.account_number}` : "";
             })()}
           />
          )}
        </div>
      </div>
    </>
  );
}
